import AsyncStorage from '@react-native-async-storage/async-storage';
import { BaseService, ClientError } from './base-service';

export const facialLoginErrorKeys = {
    InvalidFacialLogin: 'facialLoginNotMatched',
    InvalidFacialLoginChallenge: 'facialLoginSessionMissing',
    FacialPhotoMissing: 'facialLoginPhotoMissing',
    InvalidValue: 'facialLoginInvalidImage',
};

export function getFacialLoginErrorKey(code: unknown, fallback = 'facialLoginUnavailable'): string {
    if (typeof code === 'string' && Object.prototype.hasOwnProperty.call(facialLoginErrorKeys, code)) {
        return facialLoginErrorKeys[code as keyof typeof facialLoginErrorKeys];
    }
    return fallback;
}

class FacialLoginService extends BaseService {
    private facialToken: string | null = null;
    private facialExpiresAt = 0;

    setFacialToken(token: string, expiresAt: string): void {
        this.facialToken = token;
        const expiration = Date.parse(expiresAt);
        this.facialExpiresAt = Number.isFinite(expiration) ? expiration : 0;
    }

    getFacialExpiresAt(): number {
        return this.facialExpiresAt;
    }

    hasFacialToken(): boolean {
        return this.facialToken !== null;
    }

    clearFacialToken(): void {
        this.facialToken = null;
        this.facialExpiresAt = 0;
    }

    getFacialToken(): string {
        if (!this.facialToken) {
            throw new ClientError('facialLoginSessionMissing');
        }
        if (Date.now() >= this.facialExpiresAt) {
            throw new ClientError('facialLoginExpired');
        }
        return this.facialToken;
    }

    private async responseError(response: Response, fallback: string): Promise<ClientError> {
        const result = await response.json().catch(() => {
            return null;
        });
        return new ClientError(getFacialLoginErrorKey(result?.code, fallback));
    }

    async validateFacialLogin(frame: Blob, signal: AbortSignal): Promise<void> {
        if (frame.type !== 'image/jpeg' || frame.size === 0) {
            throw new ClientError('facialLoginInvalidImage');
        }
        if (frame.size > 1024 * 1024) {
            throw new ClientError('facialLoginPayloadTooLarge');
        }
        const token = this.getFacialToken();
        const headers = this.getHeaders(null);
        headers.set('Authorization', `Facial ${token}`);

        const data = new FormData();
        data.append('frame', frame, 'frame.jpg');

        const controller = new AbortController();
        const abort = () => controller.abort();
        signal.addEventListener('abort', abort, { once: true });
        const timeout = setTimeout(abort, 30000);

        try {
            if (signal.aborted) {
                abort();
            }
            const res = await fetch(`${this.centralEndpoint}/validate-facial-login`, {
                method: 'POST',
                headers,
                body: data,
                signal: controller.signal,
            });
            if (!res.ok) {
                throw await this.responseError(res, res.status >= 500 ? 'facialLoginUnavailable' : 'facialLoginRequestFailed');
            }
            const result = await res.json();
            if (controller.signal.aborted) {
                throw new DOMException('Aborted', 'AbortError');
            }
            if (token !== this.facialToken) {
                throw new ClientError('facialLoginSessionMissing');
            }
            if (Date.now() >= this.facialExpiresAt) {
                throw new ClientError('facialLoginExpired');
            }
            if (typeof result?.token !== 'string' || !result.token) {
                throw new ClientError('facialLoginRequestFailed');
            }
            await AsyncStorage.setItem('BEARER_TOKEN', result.token);
            this.clearFacialToken();
        } catch (error) {
            if (controller.signal.aborted && !signal.aborted) {
                throw new ClientError('facialLoginResponseTimeout');
            }
            throw error;
        } finally {
            clearTimeout(timeout);
            signal.removeEventListener('abort', abort);
        }
    }
}

export const facialLoginService = new FacialLoginService();
